import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Negociacao, AnaliseCadastral, NegociacaoNota } from '../../../types';
import { useToast } from '../../../src/contexts/ToastContext';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useWizardLock } from '../../../src/hooks/useWizardLock';
import Step1Identificacao from './steps/Step1Identificacao.tsx';
import Step2RelacaoUCs from './steps/Step2RelacaoUCs.tsx';
import Step3Credito from './steps/Step4Credito.tsx';
import Step4Decisao, { getPendenciasDocumentais, getBlockingStepValidation } from './steps/Step4Decisao.tsx';
import Step5Verificacao from './steps/Step5Verificacao.tsx';

import { ArrowRight, FileText, CheckCircle2, FileWarning, Clock, AlertTriangle, Play, CheckCircle, ChevronRight, ChevronLeft, Save, X, Lock, UserX, Zap, TrendingDown, Eye } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { motivoDaReprovacao } from '../../../src/lib/termoReprovado';
import { pendenciasMotivoReprovacao, descreverMotivosReprovacao } from '../../../src/lib/motivosReprovacao';
import { useMotivosReprovacao } from '../../../src/hooks/useMotivosReprovacao';
import { logUsageEvent } from '../../../src/lib/logger';
import { getBusinessElapsedMs, getCorrectStartDate } from '../../../src/lib/slaUtils';
import { recalcularCondicaoComercial } from '../../../src/lib/politicaComercialDb';
import { liberarAssinaturasInternas } from '../../../src/lib/liberarAssinaturasTermo';
import { calcularConsumoUc } from '../../../src/lib/analiseTecnicaConsumo';
import { propsFundoModal } from '../../../src/lib/backdropModal';
import { TrocaDonoAnaliseAviso } from '../../../src/components/TrocaDonoAviso';

interface AnaliseCadastralWizardProps {
    negociacaoId: number;
    onClose: () => void;
}

const steps = [
    'Relação de UCs',
    'Identificação Geral',
    'Análise de Crédito',
    'Parecer e Decisão',
    'Verificação Telefônica'
];

export default function AnaliseCadastralWizard({ negociacaoId, onClose }: AnaliseCadastralWizardProps) {
    const { addToast } = useToast();
    const { user } = useAuth();
    const { isMyLock, lockInfo, acquiring, acquireLock, releaseLock } = useWizardLock(negociacaoId, user?.id);
    // #6428: lista de motivos para a reprovação. Se não carregar, a reprovação
    // segue só com o parecer escrito (não trava a analista).
    const { motivos: motivosReprovacao, erro: erroMotivosReprovacao } = useMotivosReprovacao();
    const [lockChecked, setLockChecked] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [negociacao, setNegociacao] = useState<Negociacao | null>(null);
    const [isStep1Valid, setIsStep1Valid] = useState(false);
    const [analiseData, setAnaliseData] = useState<Partial<AnaliseCadastral>>({
        negociacao_id: negociacaoId,
        step_atual: 1
    });
    const [showDivergenceModal, setShowDivergenceModal] = useState(false);
    const [negociacaoNotas, setNegociacaoNotas] = useState<NegociacaoNota[]>([]);
    const [loadingNegociacaoNotas, setLoadingNegociacaoNotas] = useState(true);
    const [notasLidasConfirmadas, setNotasLidasConfirmadas] = useState(false);
    const [notasLidasAt, setNotasLidasAt] = useState<string | null>(null);

    const [forceViewOnly, setForceViewOnly] = useState(false);
    const [wizardChannel, setWizardChannel] = useState<any>(null);

    // ── Auto-save refs ──
    const analiseDataRef = useRef(analiseData);
    const currentStepRef = useRef(currentStep);
    const autoSaveDirty = useRef(false);
    const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const initialLoadDone = useRef(false);

    /**
     * Campos efetivamente editados nesta sessão do wizard.
     *
     * Antes, todo save gravava o objeto inteiro (`...analiseData`). Isso torna
     * qualquer estado local desatualizado DESTRUTIVO: um campo que foi escrito
     * direto no banco por outro caminho — o anexo do relatório de crédito, que o
     * Step de Crédito persiste na hora — voltava a NULL no autosave seguinte,
     * porque o estado em memória ainda tinha o valor antigo.
     *
     * Caso real (Antoniel, negociação 3011, 29/07/2026): a analista perdeu a
     * análise de crédito junto com o PDF do SPC anexado.
     *
     * Gravando só o que mudou, o wizard nunca escreve por cima de um campo que
     * ele não editou.
     */
    const dirtyKeys = useRef<Set<string>>(new Set());

    /** JWT corrente — o save do beforeunload precisa dele (fetch cru, sem o client). */
    const accessTokenRef = useRef<string | null>(null);
    useEffect(() => {
        supabase.auth.getSession().then(({ data }) => { accessTokenRef.current = data.session?.access_token ?? null; });
        const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
            accessTokenRef.current = session?.access_token ?? null;
        });
        return () => sub.subscription.unsubscribe();
    }, []);

    /** Marca campos como alterados e aplica o merge de forma funcional (sem stale closure). */
    const patchAnaliseData = useCallback((patch: Partial<AnaliseCadastral>) => {
        Object.keys(patch).forEach(k => dirtyKeys.current.add(k));
        // Atualiza o ref na mesma tick. O useEffect de sincronia só roda DEPOIS do
        // render: se um autosave disparasse nesse intervalo, ele leria o valor antigo
        // e gravaria `undefined` — que o JSON.stringify simplesmente omite. Foi assim
        // que a decisão final da análise 186 (Associação Energia) se perdeu.
        analiseDataRef.current = { ...analiseDataRef.current, ...patch };
        setAnaliseData(prev => ({ ...prev, ...patch }));
    }, []);

    /**
     * Monta o payload só com o que mudou. `null` quando não há nada pra gravar.
     * Devolve junto as chaves realmente incluídas — só elas podem ser limpas do
     * `dirtyKeys` depois do save, senão um campo que ficou de fora perde a marca
     * de sujo e nunca mais é gravado.
     */
    const buildDirtyPayload = useCallback((nextStep?: number): { payload: Record<string, any>; chaves: string[] } | null => {
        const snapshot = analiseDataRef.current as any;
        const payload: Record<string, any> = {};
        const chaves: string[] = [];
        dirtyKeys.current.forEach(k => {
            if (k === 'id' || k === 'created_at') return;
            // `undefined` seria descartado na serialização: manter sujo pra tentar de novo.
            if (snapshot[k] === undefined) return;
            payload[k] = snapshot[k];
            chaves.push(k);
        });

        const step = nextStep ?? currentStepRef.current;
        if (step !== undefined) payload.step_atual = step;

        // step_atual + updated_at sozinhos não justificam uma escrita
        if (chaves.length === 0 && step === snapshot.step_atual) return null;

        payload.updated_at = new Date().toISOString();
        return { payload, chaves };
    }, []);

    // ── Debounced Auto-save (5s after last change) ──
    const executeAutoSave = useCallback(async () => {
        const dataSnapshot = analiseDataRef.current;
        // initialLoadDone: nunca gravar com estado não hidratado — é o caminho que
        // transformaria um wizard recém-montado num apagador de linha.
        if (!dataSnapshot.id || forceViewOnly || !initialLoadDone.current) return;

        const dirty = buildDirtyPayload();
        if (!dirty) return;

        try {
            const { data: negAtual } = await supabase
                .from('negociacoes')
                .select('status')
                .eq('id', negociacaoId)
                .single();
            if (!negAtual || !['termo_assinado', 'termo_analise'].includes(negAtual.status)) return;

            const { error } = await supabase
                .from('analise_cadastral')
                .update(dirty.payload)
                .eq('id', dataSnapshot.id);
            if (error) throw error;

            // Limpa só o que foi gravado — o que mudou durante o await continua sujo.
            dirty.chaves.forEach(k => dirtyKeys.current.delete(k));
        } catch (err) {
            console.warn('[Wizard] Auto-save falhou:', err);
        }
    }, [negociacaoId, forceViewOnly, buildDirtyPayload]);

    useEffect(() => {
        if (!initialLoadDone.current || loading || !isMyLock || forceViewOnly) return;
        autoSaveDirty.current = true;
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(() => {
            if (autoSaveDirty.current) {
                autoSaveDirty.current = false;
                executeAutoSave();
            }
        }, 5000);
    }, [analiseData, currentStep]);

    // Cleanup timer + flush on unmount
    useEffect(() => {
        return () => {
            if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
            if (autoSaveDirty.current && analiseDataRef.current.id && isMyLock && !forceViewOnly) {
                executeAutoSave();
            }
        };
    }, [executeAutoSave, isMyLock, forceViewOnly]);

    // ── beforeunload: warn user + fire synchronous save ──
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!isMyLock || forceViewOnly || !analiseDataRef.current.id || !initialLoadDone.current) return;

            // Só grava o que mudou. Mandar o snapshot inteiro aqui era especialmente
            // perigoso: este caminho dispara no fechamento da aba, quando o estado
            // em memória tem mais chance de estar defasado em relação ao banco.
            const dirty = buildDirtyPayload();
            if (!dirty) return;

            e.preventDefault();
            const id = analiseDataRef.current.id;
            const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/analise_cadastral?id=eq.${id}`;
            const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
            // Precisa do JWT do usuário: com a anon key no Authorization a request
            // sai como `anon` e a RLS derruba — o save "de despedida" nunca gravava.
            const token = accessTokenRef.current;
            if (!token) return;
            try {
                fetch(url, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'apikey': anonKey,
                        'Authorization': `Bearer ${token}`,
                        'Prefer': 'return=minimal'
                    },
                    body: JSON.stringify(dirty.payload),
                    keepalive: true
                });
            } catch { /* best-effort */ }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isMyLock, forceViewOnly, buildDirtyPayload]);

    // Setup Broadcast Channel
    useEffect(() => {
        const channel = supabase.channel(`wizard_${negociacaoId}`);
        channel.subscribe((status) => {
            if (status === 'SUBSCRIBED') {
                setWizardChannel(channel);
            }
        });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [negociacaoId]);

    // Editor broadcasts state
    useEffect(() => {
        if (isMyLock && wizardChannel && !loading) {
            wizardChannel.send({
                type: 'broadcast',
                event: 'state_update',
                payload: { analiseData, negociacao, currentStep }
            });
        }
    }, [analiseData, negociacao, currentStep, isMyLock, wizardChannel, loading]);

    // Viewer listens to state
    useEffect(() => {
        if (!isMyLock && forceViewOnly && wizardChannel) {
            wizardChannel.on('broadcast', { event: 'state_update' }, (payload: any) => {
                const data = payload.payload;
                if (data.analiseData) setAnaliseData(data.analiseData);
                if (data.negociacao) setNegociacao(data.negociacao);
                if (data.currentStep) setCurrentStep(data.currentStep);
            });
        }
    }, [isMyLock, forceViewOnly, wizardChannel]);

    /** Calculate per-UC consumption divergences between vendor-registered and 12-month calculated average (excludes rejected UCs) */
    const getConsumoDivergences = () => {
        if (!negociacao?.unidades_consumidoras) return [];
        const divergences: { ucNumero: string; ucId: number; vendorConsumo: number; calcConsumo: number; diff: number; janelaCurta: boolean; mesesBase: number; variacaoPercent: number | null }[] = [];

        negociacao.unidades_consumidoras.forEach((uc: any) => {
            const dte = uc.dados_tecnicos_equatorial;
            if (!dte?.historyTable || dte.historyTable.length === 0) return;

            // Skip rejected UCs — they don't count toward global consumption
            if (dte.status === 'Reprovada') return;

            // Regra única (src/lib/analiseTecnicaConsumo.ts): em UC com GD a média
            // não descarta os meses baixos, porque lá o mês baixo é a própria usina
            // do cliente compensando.
            const calculo = calcularConsumoUc({
                historico: dte.historyTable,
                tipoLigacao: dte.tipoFornecimento || uc.tipo_ligacao,
                possuiGD: dte.checks?.possuiGD,
            });
            const avgConsumo = calculo.avgConsumo;
            if (avgConsumo === 0) return;

            const vendorConsumo = uc.consumo_medio_kwh ?? 0;
            const diff = avgConsumo - vendorConsumo;

            if (Math.abs(diff) > 50) {
                divergences.push({
                    ucNumero: uc.numero_uc,
                    ucId: uc.id,
                    vendorConsumo,
                    calcConsumo: avgConsumo,
                    diff,
                    // A janela usada precisa viajar junto: sem ela o analista vê 600
                    // virar 1.100 sem contexto nenhum e desconfia do número.
                    janelaCurta: calculo.janelaCurta,
                    mesesBase: calculo.mesesBase,
                    variacaoPercent: calculo.degrau.variacaoPercent,
                });
            }
        });

        return divergences;
    };

    // Acquire lock on mount
    useEffect(() => {
        const tryAcquire = async () => {
            await acquireLock();
            setLockChecked(true);
        };
        tryAcquire();
    }, []);

    useEffect(() => {
        fetchInitialData();
    }, [negociacaoId]);

    useEffect(() => {
        const fetchNotas = async () => {
            setLoadingNegociacaoNotas(true);
            try {
                const { data: notesData } = await supabase
                    .from('negociacao_notas')
                    .select('*')
                    .eq('negociacao_id', negociacaoId)
                    .order('created_at', { ascending: false })
                    .limit(8);

                if (notesData && notesData.length > 0) {
                    const userIds = [...new Set(notesData.map(n => n.user_id).filter(Boolean))];
                    let profilesMap: Record<string, any> = {};

                    if (userIds.length > 0) {
                        const { data: profiles } = await supabase
                            .from('profiles')
                            .select('id, full_name, avatar_url')
                            .in('id', userIds);
                        profiles?.forEach(p => {
                            profilesMap[p.id] = p;
                        });
                    }

                    const enriched = notesData.map(n => ({
                        ...n,
                        user: profilesMap[n.user_id] || undefined
                    }));

                    setNegociacaoNotas(enriched);
                } else {
                    setNegociacaoNotas([]);
                }
            } catch (error) {
                console.error('Error fetching negotiation notes:', error);
                setNegociacaoNotas([]);
            } finally {
                setLoadingNegociacaoNotas(false);
            }
        };

        fetchNotas();
    }, [negociacaoId]);

    useEffect(() => {
        const fetchConfirmacaoLeitura = async () => {
            setNotasLidasConfirmadas(false);
            setNotasLidasAt(null);

            if (!user?.id) return;

            try {
                const { data, error } = await supabase
                    .from('negociacao_logs')
                    .select('created_at')
                    .eq('negociacao_id', negociacaoId)
                    .eq('user_id', user.id)
                    .eq('acao', 'Leitura de notas confirmada')
                    .order('created_at', { ascending: false })
                    .limit(1);

                if (error) throw error;

                if (data && data.length > 0) {
                    setNotasLidasConfirmadas(true);
                    setNotasLidasAt(data[0].created_at);
                }
            } catch (error) {
                console.error('Error fetching notes read confirmation:', error);
            }
        };

        fetchConfirmacaoLeitura();
    }, [negociacaoId, user?.id]);

    // Release lock and close
    const handleCloseWithRelease = async () => {
        await handleSaveProgress();
        await releaseLock();
        onClose();
    };

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            // Fetch negociacao with related data
            const { data: negData, error: negError } = await supabase
                .from('negociacoes')
                .select(`
          *,
          unidades_consumidoras(*),
          avalistas:negociacao_avalistas(*)
        `)
                .eq('id', negociacaoId)
                .single();

            if (negError) throw negError;

            let responsavelProfile = undefined;
            if (negData.responsavel_id) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('full_name')
                    .eq('id', negData.responsavel_id)
                    .single();

                if (profileData) {
                    responsavelProfile = profileData;
                }
            }

            setNegociacao({ ...negData, responsavel: responsavelProfile });

            // Fetch existing analise_cadastral — mais recente primeiro (houve casos
            // históricos de linhas duplicadas; .single() falhava e cada sessão inseria outra)
            const { data: analiseBase, error: analiseError } = await supabase
                .from('analise_cadastral')
                .select('*')
                .eq('negociacao_id', negociacaoId)
                .order('updated_at', { ascending: false, nullsFirst: false })
                .limit(1)
                .maybeSingle();

            if (analiseBase) {
                // Hidratação = novo ponto de partida. Zera o rastreio pra não
                // regravar como "alterado" algo que só veio do banco.
                dirtyKeys.current.clear();
                setAnaliseData(analiseBase);
                if (analiseBase.step_atual) {
                    setCurrentStep(analiseBase.step_atual);
                }
            } else if (analiseError) {
                throw analiseError;
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            addToast('Erro ao buscar dados da análise', 'error');
        } finally {
            setLoading(false);
            // Mark initial load complete so auto-save doesn't fire on hydration
            setTimeout(() => { initialLoadDone.current = true; }, 500);
        }
    };

    const handleSaveProgress = async (nextStep?: number) => {
        if (!isMyLock || forceViewOnly) {
            if (nextStep) setCurrentStep(nextStep);
            return;
        }
        try {
            setSaving(true);

            // A análise só aceita gravação enquanto a negociação está em análise.
            // Bloqueia o save-on-close de uma aba antiga sobrescrevendo análise já
            // finalizada (caso Sinara: decisão alterada 11 min após a conclusão).
            const { data: negAtual, error: statusError } = await supabase
                .from('negociacoes')
                .select('status')
                .eq('id', negociacaoId)
                .single();
            if (statusError) throw statusError;
            if (!['termo_assinado', 'termo_analise'].includes(negAtual.status)) {
                addToast('Esta análise já foi finalizada em outra sessão — as alterações desta janela não foram salvas. Para editar, reabra pela tela de Gestão com justificativa.', 'error');
                return;
            }

            const now = new Date().toISOString();
            // Lê do ref, não do closure: executeFinalizar chama esta função logo após
            // um setState e precisa enxergar o valor recém-atribuído.
            const atual = analiseDataRef.current;

            if (atual.id) {
                // Grava só o que mudou nesta sessão — ver comentário em `dirtyKeys`.
                const dirty = buildDirtyPayload(nextStep);
                if (dirty) {
                    // decisao_final_at é carimbado uma vez e nunca sobrescrito.
                    if (atual.decisao_final && !atual.decisao_final_at) {
                        dirty.payload.decisao_final_at = now;
                    }
                    const { error } = await supabase
                        .from('analise_cadastral')
                        .update(dirty.payload)
                        .eq('id', atual.id);
                    if (error) throw error;
                    dirty.chaves.forEach(k => dirtyKeys.current.delete(k));
                }
            } else if (initialLoadDone.current) {
                // Sem `id` E já hidratado = a análise realmente não existe ainda.
                // O guard de initialLoadDone é essencial: um upsert disparado antes
                // da hidratação monta a linha a partir do estado inicial vazio e,
                // por causa do UNIQUE(negociacao_id), sobrescreve a análise boa com
                // os DEFAULT das colunas em vez de criar uma duplicata.
                const { id, created_at, ...cleanData } = atual as any;
                const dataToSave: any = {
                    ...cleanData,
                    step_atual: nextStep || currentStep,
                    negociacao_id: negociacaoId,
                    updated_at: now,
                };
                if (cleanData.decisao_final && !cleanData.decisao_final_at) {
                    dataToSave.decisao_final_at = now;
                }
                const { data, error } = await supabase
                    .from('analise_cadastral')
                    .upsert([dataToSave], { onConflict: 'negociacao_id' })
                    .select()
                    .single();
                if (error) throw error;
                dirtyKeys.current.clear();
                setAnaliseData(prev => ({ ...prev, ...dataToSave, id: data.id }));
            }

            if (nextStep) {
                setCurrentStep(nextStep);
            }
        } catch (error) {
            console.error('Error saving progress:', error);
            addToast('Erro ao salvar progresso', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleNext = async () => {
        await handleSaveProgress(currentStep + 1);
    };

    const handlePrevious = async () => {
        await handleSaveProgress(currentStep - 1);
    };

    const handleConfirmarLeituraNotas = async () => {
        if (notasLidasConfirmadas || !user?.id) return;

        try {
            const { data: existingLog, error: existingLogError } = await supabase
                .from('negociacao_logs')
                .select('created_at')
                .eq('negociacao_id', negociacaoId)
                .eq('user_id', user.id)
                .eq('acao', 'Leitura de notas confirmada')
                .order('created_at', { ascending: false })
                .limit(1);

            if (existingLogError) throw existingLogError;

            if (existingLog && existingLog.length > 0) {
                setNotasLidasConfirmadas(true);
                setNotasLidasAt(existingLog[0].created_at);
                return;
            }
        } catch (error) {
            console.error('Error checking existing notes read confirmation:', error);
        }

        const now = new Date().toISOString();
        setNotasLidasConfirmadas(true);
        setNotasLidasAt(now);

        try {
            await supabase.from('negociacao_logs').insert({
                negociacao_id: negociacaoId,
                acao: 'Leitura de notas confirmada',
                detalhes: 'Usuário confirmou leitura das notas no início da validação de dados.',
                user_id: user.id
            });
            logUsageEvent({ userId: user.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'confirmar_leitura_notas_analise', origem: 'acao' });
        } catch (error) {
            console.error('Error logging notes read confirmation:', error);
        }
    };

    if (acquiring || !lockChecked) {
        return (
            <div className="flex flex-col justify-center items-center h-64 gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">Verificando disponibilidade...</span>
            </div>
        );
    }

    // Blocked by another user
    if (lockChecked && !isMyLock && lockInfo && !forceViewOnly) {
        const lockedSince = new Date(lockInfo.lockedSince);
        const timeStr = lockedSince.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        return (
            <div className="flex flex-col justify-center items-center h-full p-8">
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-red-200 dark:border-red-800/50 p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-5">
                        <Lock className="w-8 h-8 text-red-500 dark:text-red-400" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                        Wizard em Uso
                    </h3>
                    <p className="text-slate-600 dark:text-slate-400 text-sm mb-4 leading-relaxed">
                        Esta validação de dados está sendo editada por outro usuário no momento.
                    </p>
                    <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 mb-6 border border-slate-200 dark:border-slate-600">
                        <div className="flex items-center justify-center gap-3 mb-2">
                            <div className="w-9 h-9 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                                <UserX className="w-5 h-5 text-primary" />
                            </div>
                            <span className="font-bold text-slate-800 dark:text-slate-100 text-base">
                                {lockInfo.lockedByName}
                            </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Editando desde <span className="font-semibold">{timeStr}</span>
                        </p>
                    </div>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mb-4">
                        O lock expira automaticamente após 15 minutos de inatividade.
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 font-bold text-sm rounded-xl transition-colors border border-slate-200 dark:border-slate-600"
                        >
                            Fechar
                        </button>
                        <button
                            onClick={() => setForceViewOnly(true)}
                            className="flex-[1.5] px-4 py-3 bg-primary hover:bg-primary/90 text-white font-bold text-sm rounded-xl transition-colors shadow-lg shadow-primary/30 flex items-center justify-center gap-2"
                        >
                            <Eye size={16} /> Assistir Analista
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!negociacao) return null;

    const requiresPhoneVerification = analiseData.decisao_final === 'Aprovado' || analiseData.decisao_final === 'Aprovado com Ressalvas';

    const mainContent = (
        <div className="flex flex-col h-full bg-white dark:bg-slate-900 overflow-hidden w-full relative shrink-0">
            <button
                onClick={handleCloseWithRelease}
                className="absolute top-4 right-4 z-50 p-2 bg-slate-100/50 dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-all shadow-sm border border-slate-200 dark:border-slate-700/50"
            >
                <X size={20} />
            </button>

            {forceViewOnly && lockInfo && (
                <div className="bg-red-500 text-white px-4 py-2 text-center text-sm font-bold flex items-center justify-center gap-2 animate-pulse shrink-0">
                    <Eye size={16} /> MODO ESPECTADOR: Assistindo {lockInfo.lockedByName} preencher em tempo real. Você não pode salvar.
                </div>
            )}

            {/* Header Area */}
            <div className="hidden sm:flex bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-8 py-6 shrink-0 flex-col gap-6">
                {/* Stepper - Centered */}
                <div className="flex items-center justify-center w-full overflow-x-auto py-10 scrollbar-hide">
                    <div className="flex items-center min-w-max">
                        {steps.map((stepName, index) => {
                            const stepNumber = index + 1;
                            const isActive = stepNumber === currentStep;
                            const isCompleted = stepNumber < currentStep;

                            return (
                                <React.Fragment key={stepName}>
                                    <div
                                        className={`flex items-center gap-2 select-none transition-colors px-2 cursor-pointer hover:opacity-80
                                                ${isActive ? 'font-bold text-slate-800 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'}
                                            `}
                                        onClick={async () => {
                                            if (stepNumber === 5 && !requiresPhoneVerification) {
                                                addToast('Para esta decisão, a análise termina em Parecer e Decisão.', 'info');
                                                return;
                                            }
                                            if (currentStep === 1 && stepNumber > 1 && !notasLidasConfirmadas) {
                                                addToast('Confirme a leitura das notas da negociação antes de avançar.', 'error');
                                                return;
                                            }
                                            if (stepNumber !== currentStep) {
                                                await handleSaveProgress(stepNumber);
                                            }
                                        }}
                                    >
                                        <div className={`
                                                w-8 h-8 rounded-full flex items-center justify-center border-2 text-sm transition-all shrink-0
                                                ${isActive ? 'border-primary text-primary bg-primary/10 dark:bg-primary/20' :
                                                isCompleted ? 'border-green-500 bg-green-500 text-white' : 'border-gray-200 dark:border-slate-700 text-gray-400 dark:text-slate-500'}
                                            `}>
                                            {isCompleted ? <CheckCircle size={16} /> : stepNumber}
                                        </div>
                                        <span className="text-sm whitespace-nowrap">{stepName}</span>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className={`h-[2px] w-8 sm:w-16 mx-1 ${isCompleted ? 'bg-green-500' : 'bg-slate-100 dark:bg-slate-800/80'}`} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Step Content */}
            <div className="flex-1 overflow-y-auto p-8 bg-slate-50/30 dark:bg-slate-900/50">
                {negociacao?.id != null && <TrocaDonoAnaliseAviso negociacaoId={Number(negociacao.id)} />}
                {currentStep === 1 && (
                    <div className={`mb-6 rounded-2xl border shadow-sm overflow-hidden ${notasLidasConfirmadas
                        ? 'border-emerald-200 dark:border-emerald-700/50 bg-emerald-50/70 dark:bg-emerald-900/15'
                        : 'border-amber-200 dark:border-amber-700/50 bg-amber-50/80 dark:bg-amber-900/15'
                        }`}>
                        <div className="px-5 py-4 border-b border-white/50 dark:border-slate-700/50 flex items-center justify-between gap-4">
                            <div>
                                <h3 className="text-sm sm:text-base font-black text-slate-800 dark:text-slate-100 uppercase tracking-wide">Notas da Negociação (Leitura Obrigatória)</h3>
                                <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">Revise as últimas notas antes de iniciar a análise técnica das UCs.</p>
                            </div>
                            {notasLidasConfirmadas && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700/50">
                                    <CheckCircle2 size={14} /> Leitura Confirmada
                                </span>
                            )}
                        </div>

                        <div className="p-4 sm:p-5 space-y-3 max-h-72 overflow-y-auto">
                            {loadingNegociacaoNotas ? (
                                <p className="text-sm text-slate-500 dark:text-slate-400">Carregando notas...</p>
                            ) : negociacaoNotas.length === 0 ? (
                                <p className="text-sm text-slate-600 dark:text-slate-300">Nenhuma nota cadastrada para esta negociação.</p>
                            ) : (
                                negociacaoNotas.map((nota) => (
                                    <div key={nota.id} className="rounded-xl border border-slate-200/80 dark:border-slate-700/60 bg-white/80 dark:bg-slate-800/40 p-3">
                                        <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                                            <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{nota.user?.full_name || 'Usuário'}</span>
                                            <span className="text-[11px] text-slate-500 dark:text-slate-400">{new Date(nota.created_at).toLocaleString('pt-BR')}</span>
                                        </div>
                                        <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{nota.conteudo}</p>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="px-5 py-4 border-t border-white/50 dark:border-slate-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                                {notasLidasConfirmadas && notasLidasAt
                                    ? `Leitura confirmada por você em ${new Date(notasLidasAt).toLocaleString('pt-BR')}`
                                    : 'Você precisa confirmar a leitura para avançar para a próxima etapa.'}
                            </div>
                            {!notasLidasConfirmadas && (
                                <button
                                    type="button"
                                    onClick={handleConfirmarLeituraNotas}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold shadow-sm transition-colors"
                                >
                                    <CheckCircle2 size={14} /> Confirmar que li as notas
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <div className={forceViewOnly ? "pointer-events-none opacity-90 select-none relative" : ""}>
                    {forceViewOnly && <div className="absolute inset-0 z-50 cursor-not-allowed"></div>}
                    {currentStep === 1 && (
                        <Step2RelacaoUCs
                            negociacao={negociacao}
                            onChange={(data) => setNegociacao({ ...negociacao, ...data })}
                            analiseData={analiseData}
                            onAnaliseChange={patchAnaliseData}
                        />
                    )}
                    {currentStep === 2 && (
                        <Step1Identificacao
                            negociacao={negociacao}
                            onChange={(data) => setNegociacao({ ...negociacao, ...data })}
                            analiseData={analiseData}
                            onAnaliseChange={patchAnaliseData}
                            documentos_json={negociacao.documentos_json}
                            onValidate={setIsStep1Valid}
                        />
                    )}
                    {currentStep === 3 && (
                        <Step3Credito
                            data={analiseData}
                            onChange={patchAnaliseData}
                            negociacaoId={negociacaoId}
                            negociacao={negociacao}
                        />
                    )}
                    {currentStep === 4 && (
                        <Step4Decisao
                            data={analiseData}
                            onChange={patchAnaliseData}
                            negociacao={negociacao}
                            motivosReprovacao={motivosReprovacao}
                            erroMotivosReprovacao={erroMotivosReprovacao}
                        />
                    )}
                    {currentStep === 5 && (
                        <Step5Verificacao
                            data={analiseData}
                            onChange={patchAnaliseData}
                            negociacao={negociacao}
                        />
                    )}
                </div>
            </div>

            {/* Footer Controls */}
            <div className="flex items-center justify-between px-8 py-6 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
                <button
                    onClick={handleCloseWithRelease}
                    className="px-4 py-2 rounded-md text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700 transition-colors shadow-sm"
                >
                    Cancelar e Sair
                </button>

                <div className="flex gap-3">
                    {currentStep > 1 && (
                        <button
                            onClick={handlePrevious}
                            disabled={saving}
                            className="flex items-center px-4 py-2 border border-slate-200 dark:border-slate-700 shadow-sm text-sm font-bold rounded-md text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4 mr-2" />
                            Voltar
                        </button>
                    )}

                    {currentStep < 5 ? (
                        <button
                            onClick={async () => {
                                if (currentStep === 1 && !notasLidasConfirmadas) {
                                    addToast('Confirme a leitura das notas da negociação para continuar.', 'error');
                                    return;
                                }

                                if (currentStep === 4) {
                                    if (requiresPhoneVerification) {
                                        const validationIssues = getBlockingStepValidation(analiseData, negociacao, motivosReprovacao);
                                        if (validationIssues.length > 0) {
                                            const issueText = validationIssues.map(v => `Etapa ${v.step} (${v.label}):\n  • ${v.issues.join('\n  • ')}`).join('\n\n');
                                            addToast(`Preencha todas as etapas antes de avançar:\n\n${issueText}`, 'error');
                                            return;
                                        }

                                        await handleNext();
                                        return;
                                    }

                                    // Este caminho finaliza sem a validação completa das etapas;
                                    // o motivo da reprovação (#6428) precisa ser cobrado aqui também.
                                    const faltaMotivo = pendenciasMotivoReprovacao(analiseData, motivosReprovacao);
                                    if (faltaMotivo.length > 0) {
                                        addToast(faltaMotivo.join('\n'), 'error');
                                        return;
                                    }

                                    await executeFinalizar();
                                    return;
                                }
                                await handleNext();
                            }}
                            disabled={saving || (currentStep === 1 && !notasLidasConfirmadas) || (currentStep === 4 && (!analiseData.decisao_final || analiseData.necessita_troca_titularidade === undefined))}
                            className="flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-500 hover:bg-green-600 disabled:opacity-50 transition-colors"
                        >
                            {currentStep === 4 && !requiresPhoneVerification ? 'Finalizar Análise' : 'Avançar'}
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </button>
                    ) : !forceViewOnly ? (
                        <button
                            onClick={async () => {
                                if (requiresPhoneVerification) {
                                    // Final check before sending for approvals
                                    const validationIssues = getBlockingStepValidation(analiseData, negociacao, motivosReprovacao);
                                    if (validationIssues.length > 0) {
                                        const issueText = validationIssues.map(v => `Etapa ${v.step} (${v.label}):\n  • ${v.issues.join('\n  • ')}`).join('\n\n');
                                        addToast(`Preencha todas as etapas antes de finalizar:\n\n${issueText}`, 'error');
                                        return;
                                    }
                                }

                                // Check for consumption divergences — show modal before finalizing
                                const divergences = getConsumoDivergences();
                                if (divergences.length > 0) {
                                    setShowDivergenceModal(true);
                                    return;
                                }

                                await executeFinalizar();
                            }}
                            disabled={saving || (requiresPhoneVerification && (() => { try { const p = JSON.parse(analiseData.verificacao_telefonica_resultado || '{}'); return !p.resultado_final; } catch { return true; } })())}
                            className="flex items-center px-6 py-2 border border-transparent text-sm font-bold rounded-md shadow-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-all active:scale-95"
                        >
                            {saving ? 'Salvando...' : 'Finalizar Análise'}
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );

    /** Core finalization logic — extracted so divergence modal can also call it */
    async function executeFinalizar() {
        // Set the analyst before saving. A mutação direta no ref é intencional:
        // handleSaveProgress roda na sequência e lê o snapshot, sem esperar o
        // re-render. Precisa marcar como alterado, senão o campo não entra no
        // payload (que agora só leva o que mudou).
        if (user?.id) {
            dirtyKeys.current.add('analista_id');
            setAnaliseData(prev => ({ ...prev, analista_id: user.id }));
            analiseData.analista_id = user.id;
            analiseDataRef.current = { ...analiseDataRef.current, analista_id: user.id };
        }

        await handleSaveProgress();

        // ── Update UC consumo_medio_kwh from analysis data ──
        const divergences = getConsumoDivergences();
        if (divergences.length > 0) {
            let newGlobalConsumo = 0;
            const ucsArr = negociacao!.unidades_consumidoras as any[];

            for (const uc of ucsArr) {
                const dte = uc.dados_tecnicos_equatorial;
                // Skip rejected UCs — they don't count toward global consumption
                if (dte?.status === 'Reprovada') continue;

                const div = divergences.find(d => d.ucId === uc.id);
                if (div) {
                    // Update this UC's consumo_medio_kwh to the calculated value
                    await supabase.from('unidades_consumidoras').update({ consumo_medio_kwh: div.calcConsumo }).eq('id', uc.id);
                    newGlobalConsumo += div.calcConsumo;
                } else {
                    newGlobalConsumo += uc.consumo_medio_kwh || 0;
                }
            }

            // Update the negotiation's global consumo_medio_kwh
            await supabase.from('negociacoes').update({ consumo_medio_kwh: newGlobalConsumo }).eq('id', negociacaoId);

            // Nova Política Comercial: volume mudou → reclassifica público e recalcula multiplicador
            try {
                await recalcularCondicaoComercial(Number(negociacaoId));
            } catch (recalcErr) {
                console.error('Erro ao reclassificar condição comercial:', recalcErr);
            }

            // Log divergence to feed (negociacao_notas)
            const { data: userData } = await supabase.auth.getUser();
            const janelaDe = (d: { janelaCurta: boolean; mesesBase: number; variacaoPercent: number | null }) =>
                d.janelaCurta
                    ? `últimos ${d.mesesBase} meses — mudança de patamar de ${d.variacaoPercent! > 0 ? '+' : ''}${d.variacaoPercent}%`
                    : `${d.mesesBase} meses do histórico`;
            const divergenceLines = divergences.map(d => `UC ${d.ucNumero}: ${d.vendorConsumo} kWh → ${d.calcConsumo} kWh (${d.diff > 0 ? '+' : ''}${d.diff} kWh, ${janelaDe(d)})`);
            await supabase.from('negociacao_notas').insert({
                negociacao_id: negociacaoId,
                conteudo: `[DIVERGÊNCIA DE CONSUMO] O consumo médio foi atualizado pela validação de dados:\n${divergenceLines.join('\n')}\nConsumo global atualizado: ${newGlobalConsumo} kWh`,
                tipo: 'nota',
                user_id: userData?.user?.id
            });

            // Notify the vendor about the divergence
            if (negociacao!.responsavel_id) {
                await supabase.from('notifications').insert({
                    user_id: negociacao!.responsavel_id,
                    type: 'consumo_divergencia',
                    title: '⚠️ Divergência de Consumo Detectada',
                    content: `A validação de dados de "${negociacao!.cliente_nome}" detectou divergências de consumo:\n${divergenceLines.join('\n')}\n\nA média sai do histórico da fatura. Quando o consumo muda de patamar (variação acima de 30%), ela passa a considerar só os 3 meses mais recentes — senão o número descreveria um consumo que o cliente não tem mais.`,
                    // Aponta pra negociação específica (padrão já usado em negotiation_assigned —
                    // ver src/lib/discountApproval.ts), não pro board genérico: o conteúdo
                    // completo com a explicação da janela de 3 meses só existe no feed da
                    // negociação, e o card do dropdown corta em 2 linhas (NotificationDropdown.tsx).
                    link: `/crm?negociacaoId=${negociacaoId}`,
                    read: false
                });
            }
        }

        // Fetch pipelines
        const { data: pipelinesData } = await supabase
            .from('pipelines')
            .select('id, name')
            .in('name', ['Pós-Venda', 'Funil de Vendas']);

        const posVendaId = pipelinesData?.find(p => p.name === 'Pós-Venda')?.id;
        const vendasId = pipelinesData?.find(p => p.name === 'Funil de Vendas')?.id;

        let novoStatus = 'termo_analise';
        let novoStage = negociacao!.stage;
        let novoPipelineId = negociacao!.pipeline_id;
        let podeReanalisar = null;

        let minutosDecorridos = null;
        if (negociacao!.data_assinatura_termo) {
            const start = getCorrectStartDate(negociacao!.data_assinatura_termo);
            if (start) {
                minutosDecorridos = Math.round(getBusinessElapsedMs(start, new Date()) / 60000);
            }
        }

        // Logic based on Verificação Telefônica & Decisão
        // "Confirmado", "Não confirmou", "Número errado", "Sem resposta"
        let verifResult = '';
        try {
            const p = JSON.parse(analiseData.verificacao_telefonica_resultado || '{}');
            verifResult = p.resultado_final || '';
        } catch { }

        if (verifResult === 'confirmado' && (analiseData.decisao_final === 'Aprovado' || analiseData.decisao_final === 'Aprovado com Ressalvas')) {
            novoStatus = 'termo_aprovado';
            if (posVendaId) { novoPipelineId = posVendaId; novoStage = 'Onboarding'; }
        } else if (verifResult === 'nao_confirmou') {
            novoStatus = 'termo_reprovado';
            novoStage = 'Termo de adesão';
            if (vendasId) novoPipelineId = vendasId;
            podeReanalisar = false;
        } else if (verifResult === 'numero_errado' || verifResult === 'sem_resposta') {
            novoStatus = 'termo_reprovado';
            novoStage = 'Termo de adesão';
            if (vendasId) novoPipelineId = vendasId;
            podeReanalisar = true;
        } else if (analiseData.decisao_final === 'Reprovado') {
            novoStatus = 'termo_reprovado';
            novoStage = 'Termo de adesão';
            if (vendasId) novoPipelineId = vendasId;
        } else {
            // Em Análise Cadastral contínua (missing validation or pending stuff)
            novoStatus = 'termo_analise';
            if (posVendaId) { novoPipelineId = posVendaId; novoStage = 'Análise cadastral'; }
        }

        const updatePayload: any = {
            status: novoStatus,
            stage: novoStage,
            pipeline_id: novoPipelineId,
            updated_at: new Date().toISOString()
        };

        if (podeReanalisar !== null) {
            updatePayload.pode_reanalisar = podeReanalisar;
        }

        // #6331: o porquê da reprovação vai no mesmo update; a DATA é o banco que
        // carimba (gatilho em negociacoes), para valer também na marcação manual.
        const motivoReprovacao = novoStatus === 'termo_reprovado'
            ? motivoDaReprovacao(verifResult, analiseData.decisao_final)
            : null;
        if (motivoReprovacao) updatePayload.termo_reprovado_motivo = motivoReprovacao;

        if (negociacao!.analise_cadastral_minutos_realizados == null) {
            updatePayload.analise_cadastral_minutos_realizados = minutosDecorridos;
        }

        // Troca de Titularidade: sinaliza a dependência apenas na primeira vez —
        // nunca regride um envio/assinatura já em andamento.
        if (analiseData.necessita_troca_titularidade && !negociacao!.troca_titularidade_status) {
            updatePayload.troca_titularidade_status = 'pendente_envio';
        }

        let { error } = await supabase.from('negociacoes').update(updatePayload).eq('id', negociacaoId);
        // Coluna do motivo ainda não criada no banco: salva sem ela em vez de travar a análise.
        if (error && updatePayload.termo_reprovado_motivo && /termo_reprovado_motivo/.test(error.message || '')) {
            delete updatePayload.termo_reprovado_motivo;
            ({ error } = await supabase.from('negociacoes').update(updatePayload).eq('id', negociacaoId));
        }

        if (error) {
            addToast(`Erro ao atualizar status: ${error.message}`, 'error');
        } else {
            const { data: userData } = await supabase.auth.getUser();
            await supabase.from('negociacao_logs').insert({
                negociacao_id: negociacaoId,
                acao: 'Validação de Dados Concluída:',
                detalhes: analiseData.decisao_final,
                user_id: userData.user?.id
            });
            logUsageEvent({ userId: userData.user?.id, modulo: 'gestao', funcionalidade: 'dashboard', acao: 'finalizar_analise_cadastral', origem: 'acao' });

            // Termo aprovado → libera as assinaturas internas (procurador + testemunhas)
            // no documento do Autentique. Best-effort: falha vira nota na negociação.
            if (novoStatus === 'termo_aprovado') {
                const liberacao = await liberarAssinaturasInternas(negociacaoId);
                if (liberacao === 'ok') {
                    addToast('Assinaturas do procurador e testemunhas liberadas no Autentique.', 'success');
                } else {
                    addToast('Não foi possível liberar as assinaturas internas automaticamente — veja a nota na negociação.', 'warning');
                }
            }

            // #6428: motivo em texto — a notificação guarda o rótulo, não o código,
            // para continuar legível se a lista mudar.
            const motivoReprovacaoTexto = descreverMotivosReprovacao(analiseDataRef.current, motivosReprovacao);

            if (negociacao!.responsavel_id) {
                const pendencias = getPendenciasDocumentais(analiseData.conferencia_docs, negociacao!, { tipo_cliente: negociacao!.tipo_cliente, is_condominio: analiseData.is_condominio || false });
                let detalhes = '';
                if (motivoReprovacaoTexto) {
                    detalhes += `\n\n🏷️ Motivo da reprovação: ${motivoReprovacaoTexto.principal}`;
                    if (motivoReprovacaoTexto.outros.length > 0) detalhes += ` (também pesou: ${motivoReprovacaoTexto.outros.join(', ')})`;
                }
                if (pendencias.length > 0) detalhes += `\n\n📌 Pendências Documentais (Etapa 1):\n- ` + pendencias.join('\n- ');
                if (analiseData.pendencias_obs_docs) detalhes += `\n\n🗒️ Obs. da Análise Documental:\n${analiseData.pendencias_obs_docs}`;
                if (analiseData.parecer_credito) detalhes += `\n\n⚖️ Parecer Consolidado de Crédito:\n${analiseData.parecer_credito}`;
                if (analiseData.ressalvas_condicoes) detalhes += `\n\n⚠️ Ressalvas ou Condições:\n${analiseData.ressalvas_condicoes}`;
                if (updatePayload.troca_titularidade_status === 'pendente_envio') {
                    detalhes += `\n\n🔄 Troca de Titularidade necessária: gere e envie o documento de Troca de Titularidade pela tela da negociação.`;
                }

                await supabase.from('notifications').insert({
                    user_id: negociacao!.responsavel_id,
                    type: 'analysis_completed',
                    title: `Validação de Dados Concluída: ${analiseData.decisao_final}`,
                    content: `A análise da negociação "${negociacao!.titulo}" foi concluída. Decisão: ${analiseData.decisao_final}.${detalhes}`,
                    link: `/crm`
                });
            }

            // Termo reprovado: notificação rica para a rede de parcerias (alimenta o
            // pop-up exibido ao abrir o site — TermoReprovadoPopup)
            if (novoStatus === 'termo_reprovado' && negociacao!.responsavel_id) {
                const { data: respProfile } = await supabase
                    .from('profiles')
                    .select('id, role, parceiro_id')
                    .eq('id', negociacao!.responsavel_id)
                    .single();

                const destinatarios = new Set<string>();
                if (respProfile?.role === 'vendedor_parceiro' || respProfile?.role === 'parceiro' || respProfile?.role === 'vendedor') destinatarios.add(respProfile.id);
                if (respProfile?.parceiro_id) destinatarios.add(respProfile.parceiro_id);

                if (destinatarios.size > 0) {
                    const divergsReprova = getConsumoDivergences();
                    const consumoProposta = negociacao!.consumo_medio_kwh || 0;
                    const somaUcsConferidas = ((negociacao!.unidades_consumidoras as any[]) || []).reduce((sum: number, uc: any) => {
                        if (uc.dados_tecnicos_equatorial?.status === 'Reprovada') return sum;
                        const div = divergsReprova.find(d => d.ucId === uc.id);
                        return sum + (div ? div.calcConsumo : (uc.consumo_medio_kwh || 0));
                    }, 0);
                    const pendenciasDoc = getPendenciasDocumentais(analiseData.conferencia_docs, negociacao!, { tipo_cliente: negociacao!.tipo_cliente, is_condominio: analiseData.is_condominio || false });

                    const metadata = {
                        negociacao_id: negociacaoId,
                        titulo: negociacao!.titulo,
                        cliente_nome: negociacao!.cliente_nome,
                        decisao: analiseData.decisao_final || null,
                        motivo: motivoReprovacaoTexto?.principal ?? null,
                        motivos_outros: motivoReprovacaoTexto?.outros ?? [],
                        verificacao: ['nao_confirmou', 'numero_errado', 'sem_resposta'].includes(verifResult) ? verifResult : null,
                        pendencias_documentais: pendenciasDoc,
                        pendencias_obs: analiseData.pendencias_obs_docs || null,
                        ressalvas: analiseData.ressalvas_condicoes || null,
                        divergencia_global: somaUcsConferidas > 0 && somaUcsConferidas !== consumoProposta
                            ? { proposta: consumoProposta, soma_ucs: somaUcsConferidas }
                            : null,
                        divergencias_ucs: divergsReprova.map(d => ({ uc: d.ucNumero, registrado: d.vendorConsumo, media_real: d.calcConsumo, diff: d.diff }))
                    };

                    await supabase.from('notifications').insert([...destinatarios].map(uid => ({
                        user_id: uid,
                        type: 'termo_reprovado',
                        title: `Termo reprovado: ${negociacao!.cliente_nome || negociacao!.titulo}`,
                        content: `A negociação "${negociacao!.titulo}" foi reprovada na validação de dados.${analiseData.decisao_final ? ` Decisão: ${analiseData.decisao_final}.` : ''}`,
                        link: '/crm',
                        metadata
                    })));
                }
            }

            addToast('Análise finalizada!', 'success');
        }
        // Não usa handleCloseWithRelease: a análise já foi salva no início desta função
        // e o status agora é final — um novo save seria bloqueado pelo guard de status.
        await releaseLock();
        onClose();
    }

    return (
        <>
            {/* Main Wizard */}
            {mainContent}

            {/* Divergence Confirmation Modal */}
            {showDivergenceModal && (() => {
                const divergences = getConsumoDivergences();
                const newGlobal = negociacao?.unidades_consumidoras?.reduce((sum: number, uc: any) => {
                    const div = divergences.find(d => d.ucId === uc.id);
                    return sum + (div ? div.calcConsumo : (uc.consumo_medio_kwh || 0));
                }, 0) ?? 0;

                return (
                    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in" {...propsFundoModal(() => setShowDivergenceModal(false))}>
                        <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                            {/* Header */}
                            <div className="flex items-center gap-3 px-6 py-5 border-b border-orange-200 dark:border-orange-800/40 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20">
                                <div className="p-2.5 bg-orange-100 dark:bg-orange-900/40 rounded-xl">
                                    <TrendingDown size={20} className="text-orange-600" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-orange-900 dark:text-orange-300">Divergência de Consumo Detectada</h3>
                                    <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">Os valores serão atualizados automaticamente</p>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-6 space-y-4">
                                <p className="text-sm text-slate-600 dark:text-slate-400">
                                    A análise detectou que o consumo registrado pelo vendedor diverge do consumo médio calculado sobre o histórico da fatura nas seguintes UCs:
                                </p>

                                <div className="space-y-2">
                                    {divergences.map(d => (
                                        <div key={d.ucId} className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/80 dark:border-slate-700/60">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2.5">
                                                    <Zap size={14} className="text-secondary" />
                                                    <span className="font-mono font-bold text-sm text-slate-700 dark:text-slate-200">{d.ucNumero}</span>
                                                </div>
                                                <div className="flex items-center gap-2 text-sm">
                                                    <span className="text-red-500 line-through font-medium">{d.vendorConsumo.toLocaleString('pt-BR')} kWh</span>
                                                    <span className="text-slate-400">→</span>
                                                    <span className="text-emerald-600 font-bold">{d.calcConsumo.toLocaleString('pt-BR')} kWh</span>
                                                </div>
                                            </div>
                                            {/* De onde saiu o número novo. Sem isso o analista vê 600 virar 1.100
                                                sem contexto e não tem como decidir se confirma. */}
                                            <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400 pl-6">
                                                {d.janelaCurta
                                                    ? `Média dos ${d.mesesBase} meses mais recentes — o consumo mudou de patamar (${d.variacaoPercent! > 0 ? '+' : ''}${d.variacaoPercent}%) e a média dos 12 meses descreveria um consumo que o cliente não tem mais.`
                                                    : `Média dos ${d.mesesBase} meses considerados no histórico.`}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex items-center justify-between p-3 bg-primary/5 dark:bg-primary/10 rounded-xl border border-primary/20">
                                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Novo Consumo Global</span>
                                    <span className="font-black text-primary text-base tabular-nums">{newGlobal.toLocaleString('pt-BR')} kWh</span>
                                </div>

                                <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/40 rounded-xl">
                                    <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                                        <strong>Ao confirmar:</strong> Os consumos das UCs e o consumo global da negociação serão atualizados, o vendedor será notificado e a divergência será registrada no feed.
                                    </p>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
                                <button
                                    onClick={() => setShowDivergenceModal(false)}
                                    className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl font-bold text-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-700"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={async () => {
                                        setShowDivergenceModal(false);
                                        await executeFinalizar();
                                    }}
                                    disabled={saving}
                                    className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-sm transition-all shadow-sm disabled:opacity-50"
                                >
                                    {saving ? 'Finalizando...' : 'Confirmar e Finalizar'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </>
    );
}
